import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationRecordingUrl } from '@/entities/conversations';
import { toastError } from '@/shared/lib/utils/toast';
import { usePhrasePlaybackStore } from './phrasePlaybackStore';

/** Реплика, которую слушают из строки списка */
export type PhrasePlaybackTarget = {
  /** Диалог, чью запись слушаем */
  conversationId: number;
  /** Начало реплики от начала записи, мс */
  startedAt: number;
  /** Конец реплики от начала записи, мс; null — конца у реплики нет */
  finishedAt: number | null;
  /**
   * Ключ одновременного воспроизведения; по умолчанию — диалог. Список, где соседние строки
   * бывают репликами одного диалога, задаёт его сам, чтобы остановка шла по строке.
   */
  playbackKey?: string;
  /** Насколько раньше начала реплики начинать, мс; по умолчанию — секунда разгона */
  leadMs?: number;
  /** Насколько дольше конца реплики играть, мс; по умолчанию — ровно по конец реплики */
  tailMs?: number;
};

/** Как запускают реплику */
type StartOptions = {
  /**
   * Запуск не по нажатию, а сопровождением звуком следом за курсором. Такой запуск сопровождение
   * не включает — его включает нажатие, — и о неудаче не сообщает: иначе на каждой реплике
   * диалога без записи всплывало бы сообщение.
   */
  isFollowing?: boolean;
};

/**
 * Прослушивание одной реплики: получает запись её диалога — дожидаясь той, что подгружает
 * интеграция-источник, — и отдаёт управление звучанием. Одним и тем же способом реплику
 * запускают и кнопка строки, и переход курсора в её текст.
 */
export const usePhrasePlayback = ({
  conversationId,
  startedAt,
  finishedAt,
  playbackKey: playbackKeyProp,
  leadMs,
  tailMs,
}: PhrasePlaybackTarget) => {
  const { t } = useTranslation('dialogs');
  const playbackKey = playbackKeyProp ?? String(conversationId);
  const isPlaying = usePhrasePlaybackStore((state) => state.playingKey === playbackKey);
  const isLoading = usePhrasePlaybackStore((state) => state.loadingKey === playbackKey);
  const isFollowEnabled = usePhrasePlaybackStore((state) => state.isFollowEnabled);
  const beginLoading = usePhrasePlaybackStore((state) => state.beginLoading);
  const play = usePhrasePlaybackStore((state) => state.play);
  const stop = usePhrasePlaybackStore((state) => state.stop);
  const finish = usePhrasePlaybackStore((state) => state.finish);
  const { requestRecordingUrl } = useConversationRecordingUrl();

  const start = useCallback(
    async ({ isFollowing = false }: StartOptions = {}) => {
      const wait = new AbortController();
      beginLoading({ key: playbackKey, cancelWait: () => wait.abort(), withFollow: !isFollowing });

      const result = await requestRecordingUrl(conversationId, wait.signal);

      // Ожидание прекратили — вручную или запуском другой реплики: чужое состояние не трогаем
      if (result.status === 'cancelled' || wait.signal.aborted) return;

      if (result.status === 'ready') {
        play({ key: playbackKey, audioUrl: result.audioUrl, startedAt, finishedAt, leadMs, tailMs });
        return;
      }

      // Неудача сопровождение не снимает: следующая реплика должна зазвучать и после той,
      // у которой записи не оказалось
      finish();
      if (isFollowing) return;
      toastError(result.status === 'absent' ? t('phraseSearch.noRecording') : t('phraseSearch.recordingFailed'));
    },
    [
      beginLoading,
      playbackKey,
      requestRecordingUrl,
      conversationId,
      play,
      startedAt,
      finishedAt,
      leadMs,
      tailMs,
      finish,
      t,
    ],
  );

  const toggle = useCallback(() => {
    // Повторное нажатие прекращает и звучание, и ожидание записи
    if (isPlaying || isLoading) {
      stop();
      return;
    }

    void start();
  }, [isPlaying, isLoading, stop, start]);

  return { isPlaying, isLoading, isFollowEnabled, start, stop, toggle };
};
